import { fabric } from 'fabric';
import { BrandColors } from '@hallmark/web.styles.colors';
import { CardContextState } from '../context/card-context';
import { FabricObject, CustomFabricObject, CardFaceData, FabricTextBox } from '../global-types';
import colorsList from '../styles/util.scss';
import { CanvasDataTypes } from './canvas-utils/canvas-data-types';
import { findTextById } from './utility/find-text-by-id';

/**
 * Checks in the fabric instance if we already loaded open type with the custom property hasLoadedOpenType
 */
export const hasFabricLoadedOpenType = () => !!(fabric as any).hasLoadedOpenType;

export const getObjectsByType = (canvas: fabric.Canvas, type: CanvasDataTypes) => {
  if (!canvas) {
    throw new Error('Canvas is undefined');
  }

  return canvas.getObjects().filter((obj) => {
    return obj.data?.type === type;
  });
};

export const getObjectByName = (name: string, currentCanvas: fabric.Canvas) => {
  return currentCanvas?.getObjects().find((object) => name !== undefined && object.name === name);
};

/**
 *
 * @param object fabric object to check if it is a photo text zone
 * @returns true if the provided object is a photo text zone
 */
export const isPhotoTextZone = (object: fabric.Object): object is fabric.Group => {
  return !!object.name?.startsWith(CanvasDataTypes.PhotoTextZone);
};

/**
 *
 * @param object fabric object to check if it is a photo zone
 * @returns true if the provided object is a photo zone
 */
export const isPhotoZone = (object: fabric.Object): object is fabric.Object => {
  return object.data?.type === CanvasDataTypes.PhotoZone;
};

/**
 *
 * @param object fabric object to check if it is a editable text
 * @returns true if the provided object is a editable text
 */
export const isEditableText = (object: fabric.Object): object is fabric.Textbox => {
  return object.data?.type === CanvasDataTypes.EditableText;
};

/**
 *
 * @param object fabric object to check if it is a user zone. By user zone we understand any zone that can be edited by users.
 * For instance: Photo Text Zones, Photo Zones and Editable Texts
 * @returns true if the provided object is either a Photo Text Zone, a Photo Zone or an Editable Text
 */
export const isUserZone = (object: fabric.Object) => {
  return isPhotoTextZone(object) || isPhotoZone(object) || isEditableText(object);
};

/**
 *
 * @param photoTextZone The photo text zone from which you will get the description group containing the label and icon
 * @returns the fabric Group inside photo text zone containing icon and label
 */
export const getPhotoTextZoneDescription = (photoTextZone: fabric.Group): fabric.Group | undefined => {
  const groups = photoTextZone.getObjects('group') as fabric.Group[];
  return groups.find((obj) => obj.name === CanvasDataTypes.PhotoTextZoneButton);
};

export const mergeLines = (textbox: fabric.Textbox) => {
  const offset = textbox.selectionEnd || 0;
  textbox.text = textbox.text?.replace(/\n$/, '');
  if (textbox.hiddenTextarea && textbox.text) {
    textbox.hiddenTextarea.value = textbox.text;
  }
  textbox.setSelectionStart(offset - 1);
  textbox.setSelectionEnd(offset);
  if (textbox.canvas) {
    textbox.render(textbox.canvas.getContext());
  }
};

export const MIN_FONT_SIZE = 16;

export const shrinkText = (textbox: fabric.Textbox, minFontSize: number = MIN_FONT_SIZE) => {
  const currentFontSize = textbox.fontSize as number;
  const newFontSize = currentFontSize - 1 < minFontSize ? minFontSize : currentFontSize - 1;
  textbox.fontSize = newFontSize;
  if (textbox.canvas) {
    textbox.render(textbox.canvas.getContext());
  }
};

export const expandText = (textbox: fabric.Textbox, maxFontSize: number) => {
  const currentFontSize = textbox.fontSize as number;
  const newFontSize = currentFontSize + 1 > maxFontSize ? maxFontSize : currentFontSize + 1;
  textbox.fontSize = newFontSize;
  if (textbox.canvas) {
    textbox.render(textbox.canvas.getContext());
  }
};

export const spaceContentEvenly = (group: fabric.Group, gap: number, start = 0) => {
  const items = group._objects;
  items.forEach((item, index) => {
    const previousItem = items[index - 1];
    item.set('left', previousItem ? (previousItem.left as number) + previousItem.getScaledWidth() + gap : start);
  });
};

export const getMaxWidthLine = (textbox: fabric.Textbox) => {
  const linesWidth = textbox.textLines.map((_, index) => textbox.measureLine(index).width);
  return Math.max(...linesWidth);
};

/**
 * rotates an object around its center
 * @param targetObject object to rotate
 * @param rotation angle for rotation
 */
export const setAngle = (targetObject: FabricObject, rotation: number) => {
  (targetObject as CustomFabricObject)._setOriginToCenter();
  (targetObject as CustomFabricObject).set('angle', rotation).setCoords();
  (targetObject as CustomFabricObject)._resetOrigin();
};

/**
 * Calculates the offset between a zone description's label width and measured char width
 * @param zoneDescription zone description containing button and label
 * @returns {number} Returns a photoTextZone's label's offset
 */
export const getZoneLabelOffset = (zoneDescription: fabric.Group): number => {
  let offset = 0;
  const zoneLabel = zoneDescription
    .getObjects()
    .find((obj) => obj.name === CanvasDataTypes.PhotoTextZoneLabel) as fabric.Text;
  if (zoneLabel) {
    const measureDiff = zoneLabel.measureLine(0).width - (zoneLabel.width as number);
    offset = measureDiff > 0 ? measureDiff : 0;
  }
  return offset;
};

/**
 * Hides an object's middle controls
 * @param targetObject target object
 */
export const hideMiddleControls = (targetObject: fabric.Object) => {
  targetObject.setControlsVisibility({
    mt: false,
    mb: false,
    ml: false,
    mr: false,
  });
};

/**
 * add event handlers for customizable texts (placeholder or user-added)
 * @param textElement the text element to add handlers to
 * @param defaultMessage the default message to display on the textbox when it hasn't been edited
 * @param [defaultColor] optional default text color to apply to default message
 *
 * @example
 *
 *  addUserTextHandlers(textElement, INITIAL_TEXT_MESSAGE, BrandColors.purple)
 */
export const addUserTextHandlers = (
  textElement: fabric.Textbox,
  defaultMessage: string,
  defaultColor: string,
  currentCanvas: fabric.Canvas,
) => {
  textElement.on('editing:entered', function (this: fabric.Textbox & { _textBeforeEdit: string }) {
    if (this._textBeforeEdit === defaultMessage) {
      const currentFill = currentCanvas.getActiveObject()?.fill;
      const currentFillMatch = Object.keys(colorsList).find(
        (key) => colorsList[`${key}`].toUpperCase() === currentFill?.toString().toUpperCase(),
      );
      this.set({
        text: '',
        fill: colorsList[`${currentFillMatch}`] || currentFill || BrandColors.Black,
        data: { ...this.data, edited: true },
      });
      if (this.hiddenTextarea) {
        this.hiddenTextarea.value = '';
      }
    }
  });
  textElement.on('editing:exited', function (this: fabric.Textbox) {
    if (this.text === '') {
      this.set({
        text: defaultMessage,
        data: { ...this.data, isEdited: false, edited: false },
      });
      return;
    }
    this.set('data', { ...this.data, isEdited: true });
  });
  textElement.on(
    'mousedblclick',
    function (this: fabric.Textbox & { _textBeforeEdit: string; __lastIsEditing: boolean }) {
      if (this._textBeforeEdit !== defaultMessage || this.__lastIsEditing) {
        this.selectAll();
      }
    },
  );
};

/**
 *
 * @param x will be image width
 * @param y will be image height
 * @param zw will be photozone width
 * @param zh will be photozone height
 * @returns scale ratio
 */
export const setPhotoZoneScaleRatio = (x: number, y: number, zw: number, zh: number) => {
  const ratio = Math.max(zh / y, zw / x);
  return ratio;
};

/**
 *   Determines if there are available photo zones in the current canvas.
 * @param canvas
 * @returns true if all the photo zones are filled.
 */
export function photoZonesAreFilled(canvas: fabric.Canvas) {
  const photoZones = getObjectsByType(canvas, CanvasDataTypes.PhotoZone);
  const slots = photoZones.filter((zone) => !zone.data?.hasContent);
  return slots.length === 0;
}
/** Determines if are photo zones awaitables in the canvas to be filled  */
export function hasAvailablePhotoZones(canvas: fabric.Canvas) {
  const photoZones = getObjectsByType(canvas, CanvasDataTypes.PhotoZone);
  const slots = photoZones.filter((zone) => !zone.data?.hasContent);
  return slots.length > 0;
}
/**
 * Determines if the card has been edited even after reloading the page
 * @param faces
 * @returns true if the card has been edited since it has an originalCanvasJson property in any of the faces.
 */
export const getIsCardEditted = (faces: CardFaceData[]) => {
  return faces.some((face) => face.originalCanvasJson);
};

export function setEditableTextHandlers(
  image: fabric.Image,
  editableInput: FabricTextBox,
  maxFontSize: number,
  maxLines: number,
  cardState: CardContextState,
) {
  image.set({ selectable: false, evented: false });
  editableInput.onDeselect = () => {
    if (editableInput.text === '') {
      image.visible = true;
    }
    editableInput.exitEditing();
    return false;
  };

  editableInput.onSelect = () => {
    image.visible = false;
    editableInput.enterEditing();
    editableInput.selectAll();
    // Search for the template text field by ID to get the IsFixed value
    const templateTextField = findTextById(cardState.cardFacesList, editableInput.data.ID);

    editableInput.set({
      data: {
        ...editableInput.data,
        originalText: templateTextField?.Text,
        isEdited: true,
        hasContent: true,
        type: CanvasDataTypes.EditableText,
        maxFontSize,
        fixedWidth: editableInput.getScaledWidth(),
        maxLines,
      },
      lockMovementX: templateTextField ? templateTextField.IsFixed : true,
      lockMovementY: templateTextField ? templateTextField.IsFixed : true,
      CanResizeTextArea: templateTextField ? templateTextField.CanResizeTextArea : true,
      CanEditFontSize: templateTextField ? templateTextField.CanEditFontSize : true,
      FontAutoSize: templateTextField ? templateTextField.FontAutoSize : false,
      CanEditFontColor: templateTextField ? templateTextField.CanEditFontColor : false,
      MustEditTextArea: templateTextField ? templateTextField.MustEditTextArea : false,
    });
    return false;
  };
}
